import React, { useCallback, useEffect, useMemo, useState } from "react";
import Calendar, {
  toBerlinISODate,
  startOfWeekMondayBerlin,
  weekDaysFromBerlin,
  addCalendarDays,
} from "components/Calendar";
import api from "utils/api";
import { adminBrandParams } from "utils/brand";
import { useTeamAdminBrand, useTeamAdminOrt } from "./TeamAdminBrandContext";
import { useTeamLocale } from "./TeamLocaleContext";
import { translateServiceLabel, isManualRevenueCalendarItem, formatEuro, formatLong, isAwaitingPaymentBooking, statusLabel } from "./formatters";
import TeamScheduleGrid from "./TeamScheduleGrid";
import TeamBookingDetailDialog from "./TeamBookingDetailDialog";
import TeamRevenueDetailDialog from "./TeamRevenueDetailDialog";
import { useTeamAdminLiveOptional } from "./TeamAdminLiveContext";
import ConfirmDialog from "components/ConfirmDialog";

const VIEW_MODES = ["day", "week", "month"];
const STATUS_OPTIONS = [
  "pending-employee-confirmation",
  "confirmed",
  "completed",
  "no-show",
];

const replaceBookingInCalendarData = (current, booking) => {
  if (!current?.days || !booking?._id) return current;
  const targetKey = booking.start ? toBerlinISODate(new Date(booking.start)) : null;
  const nextDays = Object.fromEntries(
    Object.entries(current.days).map(([key, items]) => [
      key,
      items
        .filter((item) => item?._id !== booking._id)
        .concat(targetKey === key ? [{ ...booking }] : []),
    ])
  );
  if (targetKey && !nextDays[targetKey]) {
    nextDays[targetKey] = [{ ...booking }];
  }
  return { ...current, days: nextDays };
};

const CalendarTab = () => {
  const { t, dateLocale, locale } = useTeamLocale();
  const adminBrand = useTeamAdminBrand();
  const adminOrt = useTeamAdminOrt();
  const today = new Date();
  const [viewMode, setViewMode] = useState("month");
  const [anchorDate, setAnchorDate] = useState(today);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedRevenue, setSelectedRevenue] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [noShowConfirm, setNoShowConfirm] = useState(null);
  const live = useTeamAdminLiveOptional();

  const anchorKey = toBerlinISODate(anchorDate);
  const scheduleBrand = adminBrand === "tawan-spa" ? "tawan-spa" : adminBrand === "dr-hoffmann" ? "dr-hoffmann" : "all";

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const { data: res } = await api.get("/admin/calendar", {
          params: {
            ...adminBrandParams({ brand: adminBrand, ort: adminOrt }),
            view: viewMode,
            date: anchorKey,
            year: viewYear,
            month: viewMonth + 1,
          },
        });
        setData(res);
      } catch (err) {
        if (!silent) {
          setError(err?.response?.data?.message || t("calendar.loadError"));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [viewMode, anchorKey, viewYear, viewMonth, adminBrand, adminOrt, t]
  );

  useEffect(() => {
    load({ silent: false });
  }, [load]);

  useEffect(() => {
    if (!live?.refreshTick) return;
    load({ silent: true });
  }, [live?.refreshTick, load]);

  const weekDays = useMemo(() => weekDaysFromBerlin(anchorDate), [anchorDate]);
  const selectedKey = toBerlinISODate(anchorDate);
  const daysMap = useMemo(() => data?.days ?? {}, [data?.days]);

  const daysWithBookings = useMemo(() => Object.keys(daysMap).length, [daysMap]);

  const minDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
  const maxDate = new Date(today.getFullYear(), today.getMonth() + 4, 0);

  const calendarLabels = useMemo(
    () => ({
      prev: t("calendar.prevMonth"),
      next: t("calendar.nextMonth"),
      closedDay: t("calendar.closedDay"),
      appointments: (count) => t("calendar.appointments", { count }),
    }),
    [t]
  );

  const setView = (mode) => {
    if (mode === "week") {
      const monday = startOfWeekMondayBerlin(anchorDate);
      setAnchorDate(monday);
      setViewYear(monday.getFullYear());
      setViewMonth(monday.getMonth());
    }
    setViewMode(mode);
  };

  const shiftAnchor = (deltaDays) => {
    const next = addCalendarDays(anchorDate, deltaDays);
    setAnchorDate(next);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const selectDay = (day) => {
    setAnchorDate(day);
    setViewYear(day.getFullYear());
    setViewMonth(day.getMonth());
  };

  const navLabel =
    viewMode === "day"
      ? anchorDate.toLocaleDateString(dateLocale, {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          timeZone: "Europe/Berlin",
        })
      : viewMode === "week"
        ? t("calendar.weekRange", {
            from: weekDays[0].toLocaleDateString(dateLocale, {
              day: "numeric",
              month: "short",
              timeZone: "Europe/Berlin",
            }),
            to: weekDays[6].toLocaleDateString(dateLocale, {
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: "Europe/Berlin",
            }),
          })
        : anchorDate.toLocaleDateString(dateLocale, {
            month: "long",
            year: "numeric",
            timeZone: "Europe/Berlin",
          });

  const navStep = viewMode === "day" ? 1 : viewMode === "week" ? 7 : 0;

  const layoutClass =
    viewMode === "week" || viewMode === "day"
      ? "team-calendar-layout--schedule"
      : "team-calendar-layout";

  const monthDayItems = daysMap[selectedKey] || [];

  const handleCalendarItemSelect = (item) => {
    if (isManualRevenueCalendarItem(item)) {
      setSelectedRevenue(item);
      return;
    }
    setSelectedBooking(item);
  };

  const handleStatusChange = async (booking, status) => {
    if (!booking?._id) return;
    setStatusSavingId(booking._id);
    setError(null);
    try {
      const { data: res } = await api.patch(
        `/admin/bookings/${booking._id}`,
        { status },
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      const updated = res.booking || { ...booking, status };
      setData((current) => replaceBookingInCalendarData(current, updated));
      setSelectedBooking((current) =>
        current?._id === updated._id ? { ...current, ...updated } : current
      );
    } catch (err) {
      setError(err?.response?.data?.message || t("bookings.saveError"));
    } finally {
      setStatusSavingId(null);
    }
  };

  const handleBookingSave = async (booking, updates) => {
    if (!booking?._id) return;
    setStatusSavingId(booking._id);
    setError(null);
    try {
      const { data: res } = await api.patch(
        `/admin/bookings/${booking._id}`,
        updates,
        { params: adminBrandParams({ brand: adminBrand }) }
      );
      const updated = res.booking || { ...booking, ...updates };
      setData((current) => replaceBookingInCalendarData(current, updated));
      setSelectedBooking((current) =>
        current?._id === updated._id ? { ...current, ...updated } : current
      );
    } catch (err) {
      setError(err?.response?.data?.message || t("bookings.saveError"));
      throw err;
    } finally {
      setStatusSavingId(null);
    }
  };

  const handleStatusSelectChange = (booking, status) => {
    if (status === "no-show" && booking?.status !== "no-show") {
      setNoShowConfirm(booking);
      return;
    }
    handleStatusChange(booking, status);
  };

  const confirmNoShow = async () => {
    if (!noShowConfirm) return;
    await handleStatusChange(noShowConfirm, "no-show");
    setNoShowConfirm(null);
  };

  return (
    <div className="team-calendar">
      <p className="team-lead">{t("calendar.lead")}</p>
      {error && <div className="auth-alert is-error">{error}</div>}

      <div className="team-calendar-view-tabs" role="tablist" aria-label={t("calendar.viewAria")}>
        {VIEW_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            role="tab"
            aria-selected={viewMode === mode}
            className={viewMode === mode ? "is-active" : ""}
            onClick={() => setView(mode)}
          >
            {t(`calendar.view.${mode}`)}
          </button>
        ))}
      </div>

      {viewMode !== "month" && (
        <div className="team-calendar-range-nav">
          <button type="button" onClick={() => shiftAnchor(-navStep)}>
            {t("common.back")}
          </button>
          <span>{navLabel}</span>
          <button type="button" onClick={() => shiftAnchor(navStep)}>
            {t("common.next")}
          </button>
        </div>
      )}

      <div className={layoutClass}>
        {viewMode === "week" && (
          <TeamScheduleGrid
            days={weekDays}
            daysMap={daysMap}
            scheduleBrand={scheduleBrand}
            dateLocale={dateLocale}
            t={t}
            selectedKey={selectedKey}
            today={today}
            onSelectDay={selectDay}
            onBookingSelect={handleCalendarItemSelect}
            onRevenueSelect={setSelectedRevenue}
            loading={loading}
          />
        )}

        {viewMode === "day" && (
          <TeamScheduleGrid
            days={[anchorDate]}
            daysMap={daysMap}
            scheduleBrand={scheduleBrand}
            dateLocale={dateLocale}
            t={t}
            selectedKey={selectedKey}
            today={today}
            onBookingSelect={handleCalendarItemSelect}
            onRevenueSelect={setSelectedRevenue}
            loading={loading}
          />
        )}

        {viewMode === "month" && (
          <div className="team-calendar-picker">
            <Calendar
              selectedDate={anchorDate}
              markedDays={daysMap}
              dateLocale={dateLocale}
              labels={calendarLabels}
              onMonthChange={(year, month) => {
                setViewYear(year);
                setViewMonth(month);
              }}
              onSelect={(d) => {
                setAnchorDate(d);
                setViewYear(d.getFullYear());
                setViewMonth(d.getMonth());
              }}
              minDate={minDate}
              maxDate={maxDate}
            />
            <p className="team-calendar-legend" aria-hidden="true">
              <span className="team-calendar-legend-swatch has-bookings" />
              {t("calendar.legend")}
            </p>
            {loading && (
              <p className="team-muted" style={{ marginTop: "0.75rem" }}>
                {t("common.loading")}
              </p>
            )}
            {!loading && daysWithBookings > 0 && (
              <p className="team-muted" style={{ marginTop: "0.75rem", fontSize: "0.8rem" }}>
                {t("calendar.daysWithBookings", { count: daysWithBookings })}
              </p>
            )}
          </div>
        )}

        {viewMode === "month" && (
          <div className="team-calendar-day">
            <h3>
              {anchorDate.toLocaleDateString(dateLocale, {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
                timeZone: "Europe/Berlin",
              })}
            </h3>
            {loading ? (
              <p className="team-muted">{t("common.loading")}</p>
            ) : monthDayItems.length === 0 ? (
              <div className="team-empty">{t("calendar.noDayEntries")}</div>
            ) : (
              <ul className="team-day-list">
                {monthDayItems.map((item) =>
                  isManualRevenueCalendarItem(item) ? (
                    <li key={item._id}>
                      <button
                        type="button"
                        className="team-day-item team-day-item--clickable team-day-item--revenue"
                        onClick={() => setSelectedRevenue(item)}
                      >
                        <div className="team-day-time">
                          {t("calendar.revenueEntry")}
                        </div>
                        <div>
                          <strong>{formatEuro(item.priceCents, locale)}</strong>
                          <p className="team-muted">
                            {item.massageType || t("calendar.revenueNoType")}
                          </p>
                        </div>
                      </button>
                    </li>
                  ) : (
                    <li key={item._id}>
                      <button
                        type="button"
                        className={[
                          "team-day-item",
                          "team-day-item--clickable",
                          isAwaitingPaymentBooking(item) ? "is-awaiting-payment" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => setSelectedBooking(item)}
                      >
                    <div className="team-day-time">
                      {new Date(item.start).toLocaleTimeString(dateLocale, {
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: "Europe/Berlin",
                      })}
                      <span>
                        {item.durationMinutes} {t("common.minutes")}
                      </span>
                    </div>
                    <div>
                      <strong>{translateServiceLabel(t, item)}</strong>
                      <p className="team-muted">
                        {item.customer?.firstName} {item.customer?.lastName}
                      </p>
                      {isAwaitingPaymentBooking(item) && (
                        <div className="team-day-badges">
                          <span className="badge team-badge-awaiting-payment">
                            {t("calendar.awaitingPayment")}
                          </span>
                          <span className="badge team-badge-awaiting-payment">
                            {statusLabel(t, item.status)}
                          </span>
                        </div>
                      )}
                    </div>
                      </button>
                    </li>
                  )
                )}
              </ul>
            )}
          </div>
        )}
      </div>

      <TeamRevenueDetailDialog
        open={Boolean(selectedRevenue)}
        entry={selectedRevenue}
        dateLocale={dateLocale}
        locale={locale}
        t={t}
        onClose={() => setSelectedRevenue(null)}
      />

      <TeamBookingDetailDialog
        open={Boolean(selectedBooking)}
        booking={selectedBooking}
        dateLocale={dateLocale}
        locale={locale}
        t={t}
        showSource={adminBrand === "all"}
        showBrand={adminBrand === "all"}
        statusOptions={STATUS_OPTIONS}
        statusSaving={Boolean(selectedBooking && statusSavingId === selectedBooking._id)}
        onStatusChange={handleStatusSelectChange}
        onBookingSave={handleBookingSave}
        onClose={() => setSelectedBooking(null)}
      />

      <ConfirmDialog
        open={Boolean(noShowConfirm)}
        title={t("bookings.noShowTitle")}
        message={
          noShowConfirm
            ? t("bookings.noShowConfirm", {
                name: `${noShowConfirm.customer?.firstName || ""} ${noShowConfirm.customer?.lastName || ""}`.trim(),
                when: formatLong(noShowConfirm.start, dateLocale),
              })
            : ""
        }
        confirmLabel={t("bookings.noShowConfirmBtn")}
        cancelLabel={t("common.cancel")}
        busyLabel={t("bookings.noShowBusy")}
        busy={Boolean(noShowConfirm && statusSavingId === noShowConfirm._id)}
        onConfirm={confirmNoShow}
        onCancel={() => !statusSavingId && setNoShowConfirm(null)}
      />
    </div>
  );
};

export default CalendarTab;
